/**
 * Gráfica de "Ingresos" por período (presentacional + su hook de datos).
 *
 * Réplica de la gráfica de ingresos del SSR (`advanced_stats_with_charts.html`,
 * `loadRevenueChart`): la serie de ingresos por semana/mes/año. Gestiona su período
 * (mini-pestañas "Sem. / Mes / Año") y su query (`useRevenueChart`, cacheada por
 * sede + período). Los valores se muestran con el formato de dinero abreviado del
 * SSR (`formatMoney`). El `ChartCard` contempla carga/error.
 */
import { useState } from "react";

import { DEFAULT_REVENUE_PERIOD, REVENUE_PERIOD_OPTIONS } from "../constants";
import { formatMoney, formatPeriodLabel, sortByPeriodAsc } from "../format";
import { useRevenueChart } from "../hooks/useStats";
import type { RevenuePeriod } from "../schemas";
import type { RevenueChartStats } from "../types";
import { BarChart } from "./BarChart";
import { ChartCard } from "./ChartCard";
import { PeriodTabs } from "./PeriodTabs";

interface RevenueChartProps {
  enabled: boolean;
}

/**
 * La serie de ingresos usa claves `YYYY-MM-DD` (semana, lunes), `YYYY-MM` (mes) o
 * `YYYY` (año). `formatPeriodLabel` cubre día/semana/mes; para el año, la clave es
 * directamente el año, que se muestra tal cual.
 */
function revenueLabel(key: string, period: RevenuePeriod): string {
  if (period === "year") {
    return key;
  }
  return formatPeriodLabel(key, period === "week" ? "week" : "month");
}

export function RevenueChart({ enabled }: RevenueChartProps) {
  const [period, setPeriod] = useState<RevenuePeriod>(DEFAULT_REVENUE_PERIOD);
  const query = useRevenueChart(period, enabled);

  return (
    <ChartCard<RevenueChartStats>
      title="Ingresos"
      action={
        <PeriodTabs
          value={period}
          options={REVENUE_PERIOD_OPTIONS}
          onChange={setPeriod}
          ariaLabel="Período de ingresos"
        />
      }
      isPending={query.isPending}
      isError={query.isError}
      error={query.error}
      data={query.data}
    >
      {(stats) => (
        <BarChart
          barClassName="bg-amber-500"
          data={sortByPeriodAsc(stats.data).map((point) => ({
            label: revenueLabel(point.period, period),
            value: point.revenue,
            display: formatMoney(point.revenue),
          }))}
        />
      )}
    </ChartCard>
  );
}
